//
//  sync-tokens.ts
//  Supernova CLI
//
//  Created by Jiri Trecak.
//  Copyright © Supernova.io. All rights reserved.
//

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports

import { Flags } from "@oclif/core"
import { SentryTraced } from "@sentry/nestjs"
import { z, ZodType } from "zod"

import { SentryCommand } from "../types/index.js"

// eslint-disable-next-line no-restricted-imports
import "colors"

import { FigmaTokensDataLoader } from "../utils/figma-tokens-data-loader.js"
import { getWritableVersion } from "../utils/sdk.js"

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Definition

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Configuration

const SyncDesignTokensConfigSchema = z.object({
  apiKey: z.string(),
  apiUrl: z.string().url().optional(),
  configFilePath: z.string(),
  designSystemId: z.string(),
  proxyUrl: z.string().url().optional(),
  tokenDirPath: z.string().optional(),
  tokenFilePath: z.string().optional(),
})

type SyncDesignTokensConfig = z.infer<typeof SyncDesignTokensConfigSchema>

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation

/** Command that handles synchronization with design tokens plugin */
export class SyncDesignTokens extends SentryCommand<SyncDesignTokensConfig> {
  // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
  // MARK: - Command configuration

  // How this command can be run
  static aliases = ["sync-tokens"]
  // Command help description
  static description = "Synchronize tokens from Figma Tokens plugin to Supernova workspaces"
  // Examples how to use the command
  static examples = [
    `$ @supernovaio/cli sync-tokens --apiKey="{xxx-xxx-xxx}" --designSystemId={1234} --tokenFilePath "/path/to/tokens.json" --configFilePath "/path/to/config.json"`,
    `$ @supernovaio/cli sync-tokens --apiKey="{xxx-xxx-xxx}" --designSystemId={1234} --tokenDirPath "/path/to/tokens/" --configFilePath "/path/to/config.json"`,
  ]
  // Static flags to enable / disable features
  static flags = {
    apiKey: Flags.string({ description: "API key to use for accessing Supernova instance", required: true }),
    apiUrl: Flags.string({
      description: "API url to use for accessing Supernova instance, would ignore defaults",
      hidden: true,
    }),
    configFilePath: Flags.string({ description: "Path to configuration JSON file", exclusive: [], required: true }),
    designSystemId: Flags.string({ description: "Design System to synchronize contents with", required: true }),
    proxyUrl: Flags.string({
      description: "When set, CLI will use provided proxy URL for all requests",
      hidden: true,
      required: false,
    }),
    tokenDirPath: Flags.string({
      description: "Path to directory of JSON files containing token definitions",
      exactlyOne: ["tokenDirPath", "tokenFilePath"],
    }),
    tokenFilePath: Flags.string({
      description: "Path to JSON file containing token definitions",
      exactlyOne: ["tokenDirPath", "tokenFilePath"],
    }),
  }

  // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
  // MARK: - Command runtime

  get commandId(): string {
    return SyncDesignTokens.id
  }

  get configSchema(): ZodType<SyncDesignTokensConfig> {
    return SyncDesignTokensConfigSchema
  }

  @SentryTraced()
  async run(): Promise<void> {
    const { flags } = await this.parse(SyncDesignTokens)

    // Get workspace -> design system –> version
    const { id, instance } = await getWritableVersion(flags)
    const dataLoader = new FigmaTokensDataLoader()
    const configDefinition = dataLoader.loadConfigFromPath(flags.configFilePath)
    const { settings } = configDefinition
    if (flags.dry) {
      settings.dryRun = true
    }

    const buildData = (payload: unknown) => ({
      connection: { name: "CLI" },
      ...dataLoader.loadConfigFromPathAsIs(flags.configFilePath),
      payload,
    })

    if (!flags.tokenFilePath && !flags.tokenDirPath) {
      throw new Error(`Either tokenFilePath or tokenDirPath must be provided`)
    }

    const tokenDefinition = flags.tokenDirPath
      ? await dataLoader.loadTokensFromDirectory(flags.tokenDirPath, flags.configFilePath)
      : await dataLoader.loadTokensFromPath(flags.tokenFilePath!)
    const response = (await instance.versions.writeTokenStudioData(id, buildData(tokenDefinition))) as {
      result: { logs: string[] }
    }
    if (response?.result?.logs && response.result.logs.length > 0) {
      for (const log of response.result.logs) {
        this.log(log)
      }
    }

    this.log(`\nTokens synchronized`.green)
  }
}
